import { createFileRoute } from "@tanstack/react-router";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Pin, Trash2 } from "lucide-react";
import { listIdeas, saveIdea, setIdeaStatus, deleteIdea } from "@/lib/ideas.functions";
import { useBusiness } from "@/components/business-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useOfflineFn } from "@/lib/offline";

export const Route = createFileRoute("/_authenticated/app/ideas")({
  head: () => ({
    meta: [
      { title: "Ideas — The Founders App" },
      {
        name: "description",
        content: "Capture raw ideas for each business before they slip away.",
      },
      { property: "og:title", content: "Ideas — The Founders App" },
      { property: "og:description", content: "A quiet place to park sparks, bets and half-thoughts." },
    ],
  }),
  component: IdeasPage,
});

const STATUSES = [
  { value: "spark", label: "Spark" },
  { value: "exploring", label: "Exploring" },
  { value: "doing", label: "Doing" },
  { value: "parked", label: "Parked" },
  { value: "done", label: "Done" },
] as const;

function IdeasPage() {
  const { active } = useBusiness();
  const businessId = active?.id ?? "";
  const [title, setTitle] = useState("");
  const [details, setDetails] = useState("");
  const [filter, setFilter] = useState<string>("all");
  const [busy, setBusy] = useState(false);

  const fetchIdeas = useServerFn(listIdeas);
  const runSave = useOfflineFn("saveIdea");
  const runStatus = useOfflineFn("setIdeaStatus");
  const runDelete = useOfflineFn("deleteIdea");

  const query = useQuery({
    queryKey: ["ideas", businessId],
    enabled: Boolean(businessId),
    queryFn: () => fetchIdeas({ data: { businessId } }),
  });

  const items = useMemo(
    () => (query.data ?? []).filter((i) => filter === "all" || i.status === filter),
    [query.data, filter],
  );

  const add = async () => {
    if (!title.trim() || busy) return;
    setBusy(true);
    try {
      await runSave({ data: { businessId, title, details } });
      setTitle("");
      setDetails("");
      void query.refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  if (!businessId) return null;

  return (
    <div className="space-y-8">
      <section className="vault-panel p-7">
        <p className="micro-label">Ideas</p>
        <h2 className="mt-3 text-xl font-medium">Park it before it disappears</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          No pressure to act. Write it down, move it along when it's ready.
        </p>
        <Input
          className="mt-4"
          placeholder="One line — what's the idea?"
          value={title}
          maxLength={200}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void add();
          }}
        />
        <Textarea
          className="mt-3 min-h-24 resize-none"
          placeholder="Optional detail — why it might matter, who it's for…"
          value={details}
          onChange={(e) => setDetails(e.target.value)}
        />
        <Button className="sheen mt-5" disabled={busy} onClick={() => void add()}>
          Save idea
        </Button>
      </section>

      <div className="flex flex-wrap gap-2">
        {[{ value: "all", label: "All" }, ...STATUSES].map((s) => (
          <button
            key={s.value}
            onClick={() => setFilter(s.value)}
            className={cn(
              "rounded-[4px] border border-border px-3 py-1.5 text-xs transition-colors",
              filter === s.value
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      <section className="space-y-4">
        {items.map((idea) => (
          <article key={idea.id} className="soft-panel p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="notranslate font-medium break-words">{idea.title}</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  {new Date(idea.created_at).toLocaleDateString(undefined, {
                    dateStyle: "medium",
                  })}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <button
                  aria-label={idea.pinned ? "Unpin idea" : "Pin idea"}
                  onClick={async () => {
                    await runStatus({ data: { id: idea.id, pinned: !idea.pinned } });
                    void query.refetch();
                  }}
                  className={cn(
                    "transition-colors",
                    idea.pinned ? "text-foreground" : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  <Pin className="h-4 w-4" />
                </button>
                <ConfirmDelete
                  ariaLabel="Delete idea"
                  itemLabel="this idea"
                  onConfirm={async () => {
                    await runDelete({ data: { id: idea.id } });
                    void query.refetch();
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </ConfirmDelete>
              </div>
            </div>
            {idea.details && (
              <p className="notranslate mt-3 text-sm leading-relaxed whitespace-pre-wrap text-muted-foreground">
                {idea.details}
              </p>
            )}
            <div className="mt-4 flex flex-wrap gap-1.5">
              {STATUSES.map((s) => (
                <button
                  key={s.value}
                  onClick={async () => {
                    await runStatus({ data: { id: idea.id, status: s.value } });
                    void query.refetch();
                  }}
                  className={cn(
                    "rounded-[4px] border border-border px-2.5 py-1 text-[11px] transition-colors",
                    idea.status === s.value
                      ? "bg-foreground text-background"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </article>
        ))}
        {items.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Nothing parked yet. Write one line when something crosses your mind.
          </p>
        )}
      </section>
    </div>
  );
}
